import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  Settings,
  LogOut,
  Code,
  UsersRound,
  BarChart,
  GitMerge,
  FileText,
  BookX,
  LayoutDashboard,
  ChevronsLeft,
  ChevronsRight,
  GitCompareArrows,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { ModeToggle } from "@/components/mode-toggle";
import { useSidebar } from "@/contexts/SidebarContext";

// Helper to get initials
const getInitials = (
  firstName?: string,
  lastName?: string,
  username?: string,
) => {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) {
    return firstName.substring(0, 2).toUpperCase();
  }
  if (username) {
    return username.substring(0, 2).toUpperCase();
  }
  return "??";
};

// Helper to get full avatar URL
const getAvatarUrl = (relativeUrl: string | undefined) => {
  if (!relativeUrl) return undefined;
  if (relativeUrl.match(/^https?:\/\//)) {
    return relativeUrl;
  }
  const apiUrl = (
    import.meta.env.VITE_API_URL || "http://localhost:8000"
  ).replace(/(\/api)?\/?$/, "");
  const cleanRelativeUrl = relativeUrl.startsWith("/")
    ? relativeUrl.substring(1)
    : relativeUrl;
  return `${apiUrl}/${cleanRelativeUrl}`;
};

const Sidebar: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const { isSidebarOpen, toggleSidebar, isMobileView } = useSidebar();

  // Determine visual state based on context
  const showFullContent = isSidebarOpen;

  let sidebarBaseClasses =
    "py-8 flex flex-col justify-between transition-all duration-300 ease-in-out border-r border-base-300/50 h-screen bg-base-200";
  let sidebarWidthClass = "";
  let sidebarPositionClass = "";
  let sidebarTransformClass = ""; // Renamed from sidebarVisibilityClass for clarity
  let sidebarZIndex = "z-50"; // Keep sidebar above content generally

  if (isMobileView) {
    sidebarPositionClass = "top-0 left-0"; // Mobile sidebar is an overlay
    sidebarTransformClass = "translate-x-0"; // Ensures it's on-screen for fixed positioning
    if (isSidebarOpen) {
      sidebarWidthClass = "w-64"; // Full width when open on mobile
    } else {
      sidebarWidthClass = "w-16"; // Icon rail width when minimized on mobile
    }
  } else {
    // Desktop view
    sidebarPositionClass = "sticky top-0"; // Sticky for desktop
    sidebarWidthClass = isSidebarOpen ? "max-w-min" : "w-20";
    sidebarTransformClass = ""; // No translation needed for sticky desktop sidebar
  }

  const baseNavItems = [
    { title: "Dashboard", url: "/", icon: LayoutDashboard },
    { title: "Articles", url: "/articles", icon: FileText },
    { title: "Missing Articles", url: "/missing-articles", icon: BookX },
    { title: "Patch Notes", url: "/patch-notes", icon: GitMerge },
    { title: "Statistics", url: "/statistics", icon: BarChart },
  ];

  const adminNavItems =
    isAuthenticated && (user?.superuser || user?.roles?.includes("admin"))
      ? [
          {
            title: "User Management",
            url: "/user-management",
            icon: UsersRound,
          },
          {
            title: "Pipelines",
            url: "/pipeline",
            icon: GitCompareArrows,
          },
        ]
      : [];

  const superuserNavItems =
    isAuthenticated && user?.superuser
      ? [
          {
            title: "API Documentation",
            url: "/docs",
            icon: Code,
          },
        ]
      : [];

  const navItems = [...baseNavItems, ...adminNavItems, ...superuserNavItems];

  // Prepare user details for display
  const displayName = user
    ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username
    : "";
  const initials = user
    ? getInitials(user.first_name, user.last_name, user.username)
    : "??";
  const avatarUrl = user ? getAvatarUrl(user.avatar_url) : undefined;

  return (
    <aside
      className={`${sidebarBaseClasses} ${sidebarWidthClass} ${sidebarPositionClass} ${sidebarTransformClass} ${sidebarZIndex}`}
    >
      <div className="flex h-full flex-col items-center">
        {/* Logo/Header Section with Toggle Button */}
        <ul
          className={`menu mb-8 gap-1 p-0 [&_li>*]:rounded-xl ${showFullContent ? "px-6" : "flex flex-col items-center px-0"}`}
        >
          <li>
            <Link
              to="/"
              className={`flex flex-grow items-center gap-3 transition-colors hover:bg-base-200 hover:text-primary ${!showFullContent ? "justify-center" : ""}`}
              title={
                showFullContent
                  ? "Go to Dashboard"
                  : "AI Content Manager - Go to Dashboard"
              }
            >
              <Activity className="size-7 flex-shrink-0" />
              {showFullContent && (
                <span className="bg-gradient-to-r from-base-content/80 to-base-content bg-clip-text text-xl font-semibold whitespace-nowrap text-transparent">
                  AI Content Manager
                </span>
              )}
            </Link>
            {/* Toggle Button - always visible */}
            <button
              onClick={toggleSidebar}
              className={`absolute rounded-md p-1 text-base-content/70 transition-colors hover:bg-base-300/50 ${showFullContent ? "top-1/2 -right-20 -translate-y-1/2" : "top-1/2 -right-15 -translate-y-1/2"}`}
              aria-label={isSidebarOpen ? "Minimize sidebar" : "Expand sidebar"}
            >
              {isSidebarOpen ? (
                <ChevronsLeft className="size-5" />
              ) : (
                <ChevronsRight className="size-5" />
              )}
            </button>
          </li>
        </ul>

        {/* Navigation Links */}
        <ul
          className={`menu gap-1 p-0 [&_li>*]:rounded-xl ${showFullContent ? "px-6" : "flex flex-col items-center px-0"}`}
        >
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.url ||
              (item.url === "/articles" &&
                location.pathname.startsWith("/articles/")) ||
              (item.url === "/missing-articles" &&
                location.pathname.startsWith("/missing-articles/"));
            return (
              <li
                key={item.title}
                title={!showFullContent ? item.title : undefined}
              >
                <Link
                  to={item.url}
                  className={`flex items-center gap-3 py-3 transition-all duration-200 ${showFullContent ? "px-4" : "h-12 w-12 justify-center"} ${isActive ? "bg-primary font-medium text-primary-content" : "text-base-content/70 hover:bg-base-300/50 hover:text-base-content"}`}
                >
                  <Icon
                    className={`size-5 flex-shrink-0 ${isActive ? "text-primary-content" : ""}`}
                  />
                  {showFullContent && <span>{item.title}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      <div className={`${showFullContent ? "px-6" : "px-2"}`}>
        <div className="divider my-4 before:bg-base-300/50 after:bg-base-300/50"></div>

        {/* User Section */}
        {isAuthenticated && user ? (
          <div
            className={`dropdown dropdown-top w-full ${showFullContent ? "dropdown-start" : "dropdown-center"}`}
          >
            {/* User Avatar Trigger */}
            <div
              tabIndex={0}
              role="button"
              className={`btn h-auto w-full gap-3 rounded-xl py-3 btn-ghost hover:bg-base-300/50 ${showFullContent ? "justify-start px-4" : "justify-center px-0"}`}
            >
              <div
                className={`size-10 flex-shrink-0 rounded-full ring-2 ring-base-300/50 ${
                  !avatarUrl
                    ? "flex items-center justify-center bg-primary/10 text-primary"
                    : ""
                }`}
              >
                {avatarUrl ? (
                  <img
                    className="rounded-full"
                    alt={displayName}
                    src={avatarUrl}
                  />
                ) : (
                  <span className="text-sm font-medium">{initials}</span>
                )}
              </div>
              {showFullContent && (
                <div className="grid flex-1 text-left">
                  <span className="truncate font-medium text-base-content">
                    {displayName}
                  </span>
                  <span className="truncate text-sm text-base-content/60">
                    {user.username}
                  </span>
                </div>
              )}
            </div>

            {/* User Dropdown Content */}
            <ul
              tabIndex={0}
              className={`dropdown-content menu z-[51] mb-2 ml-15 min-w-max gap-1 rounded-xl bg-base-200/95 p-2 shadow-lg backdrop-blur-xl ${showFullContent ? "" : "left-1/2 -translate-x-1/2"}`}
            >
              {/* Account Settings */}
              <li>
                <Link
                  to="/account"
                  className="flex items-center gap-3 rounded-lg px-4 py-3 hover:bg-base-300/50"
                >
                  <Settings className="size-4" />
                  Account Settings
                </Link>
              </li>
              {/* Theme Toggle */}
              <li className="px-1">
                <ModeToggle />
              </li>
              {/* Logout */}
              <li>
                <button
                  onClick={logout}
                  className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-error hover:bg-error/10"
                >
                  <LogOut className="size-4" />
                  Log out
                </button>
              </li>
            </ul>
          </div>
        ) : (
          <Link
            to="/login"
            className={`btn w-full rounded-xl btn-sm btn-primary ${!showFullContent ? "btn-square" : ""}`}
          >
            {showFullContent ? "Login" : <LogOut className="size-5" />}
          </Link>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
